import React, { CSSProperties, useMemo, useState } from 'react'
import './Information.scss'
import ReactSelectCommon from 'app_components/commons/reactSelect/containers/reactSelectCommon'
import { useForm } from 'react-hook-form'
import LabelComponent from 'app_components/commons/LabelComponent/LabelComponent'
import ButtonComponent from 'app_components/commons/Button/ButtonComponent'
import { Country, City } from 'country-state-city'
import ContactService from 'app_services/contact/contactService'
import { ScreenNotificationsUtil } from 'app_utils/screenNotification'
import { IoClose } from "react-icons/io5";

interface IOption {
  label: string
  value: string
}

const labelStyles: CSSProperties = {
  marginTop: '-9px',
  color: 'var(--danger)',
  fontSize: '12px',
  marginBottom: '-6px'
}

interface IInformationProps{
  buttonClose: () => void
  hideTitle?: boolean
  hideCloseIcon?: boolean
  containerStyles?: CSSProperties
  labelTextColor?: string
  defaultMessage?: string
  buttonText?: string
}

const Information: React.FC<IInformationProps> = ({
  buttonClose,
  hideTitle,
  hideCloseIcon,
  containerStyles,
  labelTextColor,
  defaultMessage,
  buttonText
}) => {

  const formHook = useForm()
  const contactService = useMemo(() => new ContactService(), [])
  const notificationService = useMemo(() => new ScreenNotificationsUtil(), [])
  const selectedCountry = formHook.watch('country')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const countriesToRender: IOption[] = useMemo(() => {
    return Country.getAllCountries().map((country) => ({
      label: country.name,
      value: country.isoCode
    }))
  }, [])

  const citiesToRender: IOption[] = useMemo(() => {
    if (!selectedCountry?.value) return []
    const cities = City.getCitiesOfCountry(selectedCountry.value)
    if (!cities?.length) return []
    return cities.map((city) => ({
      label: city.name,
      value: city.stateCode
    })).reduce((accum: IOption[], option: IOption) => {
      if (!accum.some((item) => item.label === option.label)) {
        accum.push(option)
      }
      return accum
    }, [])
  }, [selectedCountry])

  const handleSubmit = async (data: any) => {
    setIsLoading(true)
    const response = await contactService.sendEmail({
      city: data.city.label,
      country: data.country.label,
      emailFrom: data.emailFrom,
      message: defaultMessage?.trim()?.length ? defaultMessage : data.message,
      nameFrom: data.nameFrom,
      phoneNumber: data.phoneNumber,
      lastNameFrom: data.lastNameFrom,
    })
    if (response?.code === 200) {
      await notificationService.showSuccessTimer({
        text: 'Correo enviado exitosamente, un asesor se contactará contigo en poco tiempo.',
        timer: 5000
      })
      buttonClose()
    }
    setIsLoading(false)
  }

  const labelStyle: CSSProperties = {
    color: labelTextColor
  }

  return (
    <div className='contact-information-large-container' style={containerStyles}>
      {!hideCloseIcon && (
        <IoClose onClick={buttonClose} className='contact-information--icon'/>
      )}
      <form onSubmit={formHook.handleSubmit(handleSubmit)} className='contact-information-container'>
        {!hideTitle && (
          <p style={{fontSize: 18}}>O diligencia el formulario para contactarnos</p>
        )}
        <LabelComponent className='contact-information-label' style={labelStyle}>Nombres</LabelComponent>
        <input
          className='contact-information-input'
          name='nameFrom'
          ref={formHook.register({ required: true })}
        />
        {formHook?.errors?.nameFrom && (
          <LabelComponent {...labelStyles}>Este campo es obligatorio</LabelComponent>
        )}
        <LabelComponent className='contact-information-label' style={labelStyle}>Apellidos</LabelComponent>
        <input
          className='contact-information-input'
          name='lastNameFrom'
          ref={formHook.register({ required: true })}
        />
        {formHook?.errors?.lastNameFrom && (
          <LabelComponent {...labelStyles}>Este campo es obligatorio</LabelComponent>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
          <div>
            <LabelComponent className='contact-information-label' style={labelStyle} >País</LabelComponent>
            <ReactSelectCommon
              isMulti={false}
              options={countriesToRender}
              formHooks={formHook}
              identifier='country'
              required
            />
            {formHook?.errors?.country && (
              <LabelComponent {...labelStyles}>Este campo es obligatorio</LabelComponent>
            )}
          </div>
          <div>
            <LabelComponent className='contact-information-label' style={labelStyle}>Ciudad</LabelComponent>
            <ReactSelectCommon
              isMulti={false}
              options={citiesToRender}
              formHooks={formHook}
              identifier='city'
              required
            />
            {formHook?.errors?.city && (
              <LabelComponent {...labelStyles}>Este campo es obligatorio</LabelComponent>
            )}
          </div>
        </div>
        <LabelComponent className='contact-information-label' style={labelStyle}>Teléfono</LabelComponent>
        <input
          className='contact-information-input'
          name='phoneNumber'
          ref={formHook.register({ required: true })}
        />
        {formHook?.errors?.phoneNumber && (
          <LabelComponent {...labelStyles}>Este campo es obligatorio</LabelComponent>
        )}
        <LabelComponent className='contact-information-label' style={labelStyle}>Correo</LabelComponent>
        <input
          className='contact-information-input'
          name='emailFrom'
          ref={formHook.register({ required: true })}
          type='email'
        />
        {formHook?.errors?.emailFrom && (
          <LabelComponent {...labelStyles}>Este campo es obligatorio</LabelComponent>
        )}
        {!defaultMessage && (
          <>
            <LabelComponent className='contact-information-label' style={labelStyle}>Mensaje</LabelComponent>
            <textarea
              placeholder='Escríbenos el curso o tema de tu interés'
              className='contact-information-input-message'
              name='message'
              ref={formHook.register({ required: true })}
            />
            {formHook?.errors?.message && (
              <LabelComponent {...labelStyles}>Este campo es obligatorio</LabelComponent>
            )}
          </>
        )}
        <ButtonComponent
          variant='secondary'
          textTransform='none'
          borderRadius={20}
          height={40}
          margin={7}
          type='submit'
          loading={isLoading}
        >
          {buttonText?.trim()?.length ? buttonText : 'Enviar solicitud'}
        </ButtonComponent>
      </form>
    </div>

  )
}

export default Information
